import { List, Record } from 'immutable'
import { Attachment, IAttachment } from './Attachment'
import {
  CityProfileSpecialFile,
  ICityProfileSpecialFile,
} from './CityProfileSpecialFile'
import { Demographics, IDemographics } from './Demographics'
import { INationalPlace, NationalPlace } from './NationalPlace'

export interface ICityProfile {
  readonly attachments: List<IAttachment>
  readonly id: string
  readonly photos: List<IAttachment>
  readonly regionalMaps: List<IAttachment>
  readonly retailNodeMaps: List<IAttachment>
  readonly competitorsMaps: List<IAttachment>
  readonly trafficCountMaps: List<IAttachment>
  readonly topBrokers: List<IAttachment>
  readonly chamberOfCommerceFiles: List<IAttachment>
  readonly place: INationalPlace
  readonly slug: string
  readonly overview: string
  readonly demographics?: IDemographics
  readonly specialFiles: List<ICityProfileSpecialFile>
}

const CityProfileRecord = Record({
  attachments: List<Attachment>(),
  chamberOfCommerceFiles: List<Attachment>(),
  competitorsMaps: List<Attachment>(),
  demographics: undefined,
  id: '',
  overview: '',
  photos: List<Attachment>(),
  place: new NationalPlace(),
  regionalMaps: List<Attachment>(),
  retailNodeMaps: List<Attachment>(),
  slug: '',
  specialFiles: List<CityProfileSpecialFile>(),
  topBrokers: List<Attachment>(),
  trafficCountMaps: List<Attachment>(),
})

export class CityProfile extends CityProfileRecord implements ICityProfile {
  public static fromJS(initialState?: Partial<ICityProfile>) {
    if (!initialState) {
      return new CityProfile()
    }

    const {
      attachments,
      chamberOfCommerceFiles,
      competitorsMaps,
      demographics,
      photos,
      place,
      regionalMaps,
      retailNodeMaps,
      specialFiles,
      topBrokers,
      trafficCountMaps,
      ...rest
    } = initialState

    return new CityProfile({
      attachments: attachments
        ? List(attachments.map((a) => Attachment.fromJS(a)))
        : undefined,
      chamberOfCommerceFiles: chamberOfCommerceFiles
        ? List(chamberOfCommerceFiles.map((a) => Attachment.fromJS(a)))
        : undefined,
      competitorsMaps: competitorsMaps
        ? List(competitorsMaps.map((a) => Attachment.fromJS(a)))
        : undefined,
      demographics: demographics
        ? Demographics.fromJS(demographics)
        : undefined,
      photos: photos
        ? List(photos.map((a) => Attachment.fromJS(a)))
        : undefined,
      place: NationalPlace.fromJS(place),
      regionalMaps: regionalMaps
        ? List(regionalMaps.map((a) => Attachment.fromJS(a)))
        : undefined,
      retailNodeMaps: retailNodeMaps
        ? List(retailNodeMaps.map((a) => Attachment.fromJS(a)))
        : undefined,
      specialFiles: specialFiles
        ? List(specialFiles.map((a) => CityProfileSpecialFile.fromJS(a)))
        : undefined,
      topBrokers: topBrokers
        ? List(topBrokers.map((a) => Attachment.fromJS(a)))
        : undefined,
      trafficCountMaps: trafficCountMaps
        ? List(trafficCountMaps.map((a) => Attachment.fromJS(a)))
        : undefined,
      ...rest,
    })
  }

  public readonly attachments: List<Attachment>
  public readonly id: string
  public readonly photos: List<Attachment>
  public readonly regionalMaps: List<Attachment>
  public readonly retailNodeMaps: List<Attachment>
  public readonly competitorsMaps: List<Attachment>
  public readonly trafficCountMaps: List<Attachment>
  public readonly topBrokers: List<Attachment>
  public readonly chamberOfCommerceFiles: List<Attachment>
  public readonly place: NationalPlace
  public readonly slug: string
  public readonly overview: string
  public readonly demographics?: Demographics
  public readonly specialFiles: List<CityProfileSpecialFile>

  constructor(initialState?: Partial<CityProfile>) {
    initialState ? super(initialState) : super()
  }
}
